import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { zodResolver } from '@hookform/resolvers/zod';
import { RootState } from 'store';

import { Add, Close } from '@mui/icons-material';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Autocomplete,
  Box,
  Drawer,
  FormHelperText,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';

import CustomButton from 'components/Button';
import DeleteConfirmationDialog from 'components/DeleteConfirmationDialog';
import { Header } from 'components/GlobalStyles/styles';
import LabelledTextField from 'components/LabelledTextField';
import { MessageType } from 'components/SnackBar';
import { useCreateClientMutation, useUpdateClientByIdMutation } from 'services/busmanApi';
import { ClientRequest } from 'types/ClientTypes';
import { TaxCodeResponse } from 'types/TaxcodeTypes';
import { AddEditClientSchema, ClientFormValues } from 'utils/schema';

import { FormSection } from '../../ProjectManagement/styles';

import TaxCodeModal from './TaxCodeModal';

interface ClientDrawerProps {
  initialValues: ClientRequest | null;
  open: boolean;
  isEditing?: boolean;
  onClose: () => void;
  setSnackBarMessage: (message: MessageType) => void;
}

const defaultClientValue: ClientFormValues = {
  tax_code: '',
  client_id: '',
  display_id: '',
  client_name: '',
  is_active: true,
  is_individual: false,
  hourly_billing_rate: undefined,
  client_contact_firstname: '',
  client_contact_lastname: '',
  address: {
    street: '',
    city: '',
    state: '',
    country: '',
    postal_code: '',
    phone_number: '',
    fax: '',
    email: '',
    website: '',
    contact_name: '',
  },
};

const ClientDrawer: React.FC<ClientDrawerProps> = ({ initialValues, open, onClose, isEditing, setSnackBarMessage }) => {
  const theme = useTheme();

  const [createClient] = useCreateClientMutation();
  const [updateClient] = useUpdateClientByIdMutation();

  const taxCodes = useSelector((state: RootState) => state.miscReducer.taxCodes);

  const [showDiscardConfirm, setShowDiscardConfirm] = React.useState(false);
  const [showTaxCodeModal, setShowTaxCodeModal] = React.useState(false);
  const [editingTaxCode, setEditingTaxCode] = React.useState<TaxCodeResponse | null>(null);

  const handleAddTaxCode = () => {
    setEditingTaxCode(null);
    setShowTaxCodeModal(true);
  };

  const handleEditTaxCode = (taxId: string) => {
    const taxCode = (taxCodes ?? []).find((code) => code.tax_id === taxId);
    if (!taxCode) return;
    setEditingTaxCode(taxCode);
    setShowTaxCodeModal(true);
  };

  const handleCloseTaxCodeModal = () => {
    setShowTaxCodeModal(false);
    setEditingTaxCode(null);
  };

  const normalizedInitialValues = React.useMemo<ClientFormValues | null>(
    () =>
      initialValues
        ? {
            ...initialValues,
            tax_code: initialValues.tax_code ?? '',
            hourly_billing_rate:
              initialValues.hourly_billing_rate != null ? Number(initialValues.hourly_billing_rate) : undefined,
          }
        : null,
    [initialValues],
  );

  const {
    handleSubmit,
    control,
    reset,
    setValue,
    formState: { isDirty },
  } = useForm<ClientFormValues>({
    defaultValues: normalizedInitialValues ?? defaultClientValue,
    resolver: zodResolver(AddEditClientSchema),
  });

  React.useEffect(() => {
    if (open) {
      reset(normalizedInitialValues ?? defaultClientValue);
    }
  }, [open, normalizedInitialValues, reset]);

  const closeDrawer = () => {
    // Move focus out of the drawer before it gets aria-hidden on close,
    // otherwise the browser warns about hiding a focused element.
    (document.activeElement as HTMLElement | null)?.blur();
    reset(initialValues ?? defaultClientValue);
    setShowDiscardConfirm(false);
    onClose();
  };

  const handleClose = () => {
    if (isDirty) {
      setShowDiscardConfirm(true);
      return;
    }
    closeDrawer();
  };

  const onSubmit = async (data: ClientFormValues) => {
    const contactName = [data.client_contact_firstname, data.client_contact_lastname].filter(Boolean).join(' ').trim();

    const payload: ClientRequest = {
      ...data,
      tax_code_id: data.tax_code,
      ...(contactName && data.address && { address: { ...data.address, contact_name: contactName } }),
    };

    if (isEditing) {
      const result = await updateClient(payload);

      if ('error' in result) {
        setSnackBarMessage({ type: 'error', msg: `Error updating client due to, ${result.error}` });
        return;
      }

      setSnackBarMessage({ type: 'success', msg: 'Client updated successfully.' });
    } else {
      const result = await createClient(payload);

      if ('error' in result) {
        setSnackBarMessage({ type: 'error', msg: `Error adding client due to, ${result.error}` });
        return;
      }

      setSnackBarMessage({ type: 'success', msg: 'Client added successfully.' });
    }

    closeDrawer();
  };

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description">
        <Box
          role="presentation"
          sx={{ borderRadius: 2, width: { xs: '100vw', sm: 480 }, maxWidth: '100vw', p: { xs: 2, sm: 3 } }}>
          <Header>
            <Typography variant="h4">{isEditing ? 'Edit Client' : 'New Client'}</Typography>
            <Box onClick={handleClose} sx={{ cursor: 'pointer' }}>
              <Close sx={{ color: 'text.disabled', fontSize: '18px' }} />
            </Box>
          </Header>

          <FormSection component="form" onSubmit={handleSubmit(onSubmit)}>
            <Stack direction="row">
              <LabelledTextField
                dataTestId="client-input-name"
                name="client_name"
                label=" CLIENT NAME"
                control={control}
              />
            </Stack>

            <Stack direction="row" spacing={2} sx={{ width: '100%' }}>
              <Stack spacing={0.5} sx={{ width: '100%' }}>
                <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '11px' }}>
                  CLIENT TYPE
                </Typography>
                <Controller
                  name="is_individual"
                  control={control}
                  render={({ field }) => (
                    <ToggleButtonGroup
                      exclusive
                      fullWidth
                      size="small"
                      value={field.value ?? false}
                      onChange={(_event, value) => {
                        if (value !== null) field.onChange(value);
                      }}
                      sx={{
                        '& .MuiToggleButton-root.Mui-selected': {
                          backgroundColor: theme.palette.busmanColors.busmanPrimary,
                          color: theme.palette.common.white,
                          '&:hover': {
                            backgroundColor: theme.palette.busmanColors.busmanPrimary,
                          },
                        },
                      }}>
                      <ToggleButton value={false} data-testid="client-toggle-business">
                        Business
                      </ToggleButton>
                      <ToggleButton value={true} data-testid="client-toggle-individual">
                        Individual
                      </ToggleButton>
                    </ToggleButtonGroup>
                  )}
                />
              </Stack>

              <Stack spacing={0.5} sx={{ width: '100%' }}>
                <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '11px' }}>
                  STATUS
                </Typography>
                <Controller
                  name="is_active"
                  control={control}
                  render={({ field }) => (
                    <ToggleButtonGroup
                      exclusive
                      fullWidth
                      size="small"
                      value={field.value ?? true}
                      onChange={(_event, value) => {
                        if (value !== null) field.onChange(value);
                      }}
                      sx={{
                        '& .MuiToggleButton-root.Mui-selected': {
                          backgroundColor: theme.palette.busmanColors.busmanPrimary,
                          color: theme.palette.common.white,
                          '&:hover': {
                            backgroundColor: theme.palette.busmanColors.busmanPrimary,
                          },
                        },
                      }}>
                      <ToggleButton value={true} data-testid="client-toggle-active">
                        Active
                      </ToggleButton>
                      <ToggleButton value={false} data-testid="client-toggle-inactive">
                        Inactive
                      </ToggleButton>
                    </ToggleButtonGroup>
                  )}
                />
              </Stack>
            </Stack>

            {/* Tax Code Field */}
            <Stack direction="row">
              <Controller
                name="tax_code"
                control={control}
                render={({ field, fieldState: { error } }) => {
                  const taxCodeList = taxCodes ?? [];
                  const getTaxCodeLabel = (option: (typeof taxCodeList)[number]) =>
                    option.description ? `${option.code} - ${option.description}` : option.code;
                  return (
                    <Stack spacing={0.5} sx={{ width: '100%' }}>
                      <Typography variant="body2">TAX CODE</Typography>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%' }}>
                        <Autocomplete
                          size="small"
                          options={taxCodeList}
                          value={taxCodeList.find((taxCode) => taxCode.tax_id === field.value) ?? null}
                          getOptionLabel={getTaxCodeLabel}
                          isOptionEqualToValue={(option, value) => option.tax_id === value?.tax_id}
                          onChange={(_event, value) => field.onChange(value?.tax_id ?? '')}
                          ListboxProps={{ style: { fontSize: '12px' } }}
                          sx={{ width: '100%' }}
                          data-testid="client-taxcode-select"
                          popupIcon={<ExpandMoreIcon fontSize="small" />}
                          renderOption={({ key, ...optionProps }, option) => (
                            <Tooltip key={key} title={`${option.description} - ${option.rate}%`} placement="top">
                              <li {...optionProps}>{getTaxCodeLabel(option)}</li>
                            </Tooltip>
                          )}
                          // renderInput={(params) => <TextField {...params} error={!!error} />}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              error={!!error}
                              InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                  <>
                                    {field.value && (
                                      <InputAdornment position="end" sx={{ mr: -1 }}>
                                        <IconButton
                                          size="small"
                                          data-testid="client-taxcode-edit"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleEditTaxCode(field.value);
                                          }}>
                                          <EditIcon fontSize="small" />
                                        </IconButton>
                                      </InputAdornment>
                                    )}
                                    {params.InputProps.endAdornment}
                                  </>
                                ),
                              }}
                            />
                          )}
                        />
                        <Tooltip title="Add new tax code">
                          <IconButton
                            size="small"
                            data-testid="client-taxcode-add"
                            onClick={handleAddTaxCode}
                            sx={{
                              border: `1px solid ${theme.palette.divider}`,
                              borderRadius: '2px',
                              color: theme.palette.busmanColors.busmanPrimary,
                            }}>
                            <Add fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                      {error && <FormHelperText sx={{ color: 'red' }}>*{error.message || error.type}</FormHelperText>}
                    </Stack>
                  );
                }}
              />
            </Stack>
            <Stack direction="row">
              <LabelledTextField
                dataTestId="billing-rate-input"
                name="hourly_billing_rate"
                label=" HOURLY BILLING RATE"
                control={control}
                type="number"
                valueAsNumber
              />
            </Stack>

            <Stack spacing={2} sx={{ width: '100%' }}>
              <Typography variant="h6" sx={{ color: 'text.secondary', fontSize: '13px' }}>
                ADDRESS
              </Typography>

              <Stack direction="row" spacing={2} sx={{ width: '100%' }}>
                <LabelledTextField
                  dataTestId="contact-firstname-input"
                  name="client_contact_firstname"
                  label="FIRST NAME"
                  control={control}
                />
                <LabelledTextField
                  dataTestId="contact-lastname-input"
                  name="client_contact_lastname"
                  label="LAST NAME"
                  control={control}
                />
              </Stack>

              <Stack direction="row">
                <LabelledTextField
                  dataTestId="address-email-input"
                  name="address.email"
                  label=" EMAIL"
                  type="email"
                  control={control}
                />
              </Stack>

              <Stack direction="row" spacing={2} sx={{ width: '100%' }}>
                <LabelledTextField
                  dataTestId="address-phone-input"
                  name="address.phone_number"
                  label=" PHONE NUMBER"
                  control={control}
                />
                <LabelledTextField dataTestId="address-fax-input" name="address.fax" label=" FAX" control={control} />
              </Stack>

              <Stack direction="row">
                <LabelledTextField
                  dataTestId="address-street-input"
                  name="address.street"
                  label=" STREET"
                  control={control}
                />
              </Stack>

              <Stack direction="row" spacing={2} sx={{ width: '100%' }}>
                <LabelledTextField
                  dataTestId="address-city-input"
                  name="address.city"
                  label=" CITY"
                  control={control}
                />
                <LabelledTextField
                  dataTestId="address-state-input"
                  name="address.state"
                  label=" STATE"
                  control={control}
                />
              </Stack>

              <Stack direction="row" spacing={2} sx={{ width: '100%' }}>
                <LabelledTextField
                  dataTestId="address-postal-code-input"
                  name="address.postal_code"
                  label=" POSTAL CODE"
                  control={control}
                />
                <LabelledTextField
                  dataTestId="address-country-input"
                  name="address.country"
                  label=" COUNTRY"
                  control={control}
                />
              </Stack>

              <Stack direction="row">
                <LabelledTextField
                  dataTestId="address-website-input"
                  name="address.website"
                  label=" WEBSITE"
                  control={control}
                />
              </Stack>
            </Stack>

            <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ width: '100%' }}>
              <CustomButton
                testid="client-cancel-button"
                variant="contained_grey"
                width="medium"
                text="Cancel"
                onClick={handleClose}
              />
              <CustomButton
                testid="client-submit-button"
                type="submit"
                variant="contained"
                width="medium"
                text={isEditing ? 'Update' : 'Save'}
              />
            </Stack>
          </FormSection>
        </Box>
      </Drawer>

      {showDiscardConfirm && (
        <DeleteConfirmationDialog
          title="Discard changes?"
          description="You have unsaved changes. Are you sure you want to close without saving?"
          buttonText="Discard"
          onClose={() => setShowDiscardConfirm(false)}
          onDelete={closeDrawer}
        />
      )}

      <TaxCodeModal
        open={showTaxCodeModal}
        onClose={handleCloseTaxCodeModal}
        setSnackBarMessage={setSnackBarMessage}
        taxCode={editingTaxCode}
        onCreated={(taxCode) => taxCode.tax_id && setValue('tax_code', taxCode.tax_id, { shouldDirty: true })}
      />
    </>
  );
};

export default ClientDrawer;
